import { api } from "../core/api.js";
import { h, mount, onActivate } from "../core/dom.js";
import { navigate } from "../core/router.js";
import { t } from "../core/i18n.js";
import { emptyState, errorState, errorMessage, spinner } from "../core/states.js";

/**
 * Search (04-web §2.8, 01 §3.S). GET /v1/search?q=&type=&mode=fts → results list.
 * Title results link to /t/:id.
 */

interface SearchResult {
  doc_type: "title" | "cut" | "short" | "user";
  id: string;
  title?: string;
  handle?: string;
  snippet?: string;
  score?: number;
}
interface SearchResponse {
  data: SearchResult[];
  page?: { next_cursor?: string | null };
}

export function searchView(_params: Record<string, string>, root: HTMLElement): void {
  const input = h("input", {
    class: "field",
    type: "search",
    placeholder: t("search.placeholder"),
    "aria-label": t("search.title"),
    value: initialQuery(),
  }) as HTMLInputElement;
  const results = h("div", { role: "region", "aria-live": "polite", "aria-label": t("search.title") });
  const button = h("button", { type: "button", class: "btn" }, t("common.search")) as HTMLButtonElement;

  const run = async (): Promise<void> => {
    const q = input.value.trim();
    if (!q) {
      results.replaceChildren(emptyState(t("search.prompt")));
      return;
    }
    history.replaceState({}, "", `/search?q=${encodeURIComponent(q)}`);
    results.replaceChildren(spinner(t("common.searching")));
    try {
      const res = await api.get<SearchResponse>(`/v1/search?q=${encodeURIComponent(q)}`);
      if (!res.data || res.data.length === 0) {
        results.replaceChildren(emptyState(t("search.noResults")));
        return;
      }
      results.replaceChildren(...res.data.map(resultRow));
    } catch (e) {
      results.replaceChildren(errorState(t("search.failed", { msg: errorMessage(e) }), () => void run()));
    }
  };

  button.addEventListener("click", () => void run());
  input.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") void run();
  });

  mount(
    root,
    h(
      "section",
      {},
      h("h1", {}, t("search.title")),
      h(
        "form",
        {
          role: "search",
          style: "display:flex;gap:8px;align-items:flex-start",
          onsubmit: (e: Event) => {
            e.preventDefault();
            void run();
          },
        },
        h("div", { style: "flex:1" }, input),
        button,
      ),
      results,
    ),
  );

  if (input.value.trim()) void run();
}

function resultRow(r: SearchResult): HTMLElement {
  const label = r.title ?? (r.handle ? `@${r.handle}` : r.id);
  const activate = (): void => {
    if (r.doc_type === "title") navigate(`/t/${r.id}`);
    else if (r.doc_type === "user" && r.handle) navigate(`/u/${r.handle}`);
    else if (r.doc_type === "short") navigate(`/shorts/${r.id}`);
  };
  const row = h(
    "div",
    { class: "card", style: "padding:12px;margin:8px 0", role: "button", tabindex: "0", "aria-label": label },
    h("div", { style: "display:flex;gap:10px;align-items:center" }, h("span", { class: "badge" }, r.doc_type), h("strong", {}, label)),
    r.snippet ? h("p", { class: "muted", style: "margin:6px 0 0;font-size:13px" }, r.snippet) : null,
  );
  onActivate(row, activate);
  return row;
}

function initialQuery(): string {
  return new URLSearchParams(location.search).get("q") ?? "";
}
